import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SpinnerGap, EnvelopeSimple, Lock, GoogleLogo, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userType = searchParams.get('type') || 'contractor';
  const sessionExpired = searchParams.get('expired') === 'true';
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Show session expired message
  React.useEffect(() => {
    if (sessionExpired) {
      toast({
        title: 'Session Expired',
        description: 'Please log in again to continue.',
        variant: 'destructive'
      });
    }
  }, [sessionExpired]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { authAPI } = await import('../api/api');
      const response = await authAPI.login({
        ...formData,
        two_factor_code: requires2FA ? twoFactorCode : undefined,
        remember_me: rememberMe
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        toast({
          title: '2FA Required',
          description: 'Enter your authenticator code to continue',
        });
        return;
      }
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userType', response.data.user.user_type);
        localStorage.setItem('userId', response.data.user._id);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast({
          title: 'Login Successful!',
          description: `Welcome back! Redirecting to your dashboard...`,
        });

        setTimeout(() => {
          const redirect = new URLSearchParams(window.location.search).get('redirect');
          if (redirect && /^\/(?![\/\\])/.test(redirect)) {
            window.location.href = redirect;
          } else if (response.data.user.user_type === 'admin') {
            window.location.href = '/admin';
          } else if (response.data.user.user_type === 'contractor') {
            window.location.href = '/contractor-dashboard';
          } else if (response.data.user.user_type === 'homeowner') {
            window.location.href = '/homeowner-dashboard';
          } else {
            window.location.href = '/worker-dashboard';
          }
        }, 1000);
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Invalid email or password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center py-12 px-4 transition-colors duration-300" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-neutral-900 dark:text-white">
            {requires2FA ? 'Two-Factor Authentication' : 'Welcome Back'}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            {requires2FA ? 'Enter the code from your authenticator app' : 'Sign in to your account'}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          
          {requires2FA ? (
            /* 2FA Step */
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center border-2 border-black shadow-[3px_3px_0px_#000]">
                  <ShieldCheck size={40} weight="bold" className="text-white" />
                </div>
              </div>
              
              <div>
                <Label htmlFor="twoFactorCode" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">
                  Verification Code
                </Label>
                <Input
                  id="twoFactorCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="h-16 text-center text-2xl tracking-widest font-mono border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                  autoFocus
                  data-testid="2fa-code-input"
                />
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 text-center">
                  Or use a backup code
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || twoFactorCode.length < 6}
                className="w-full h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="verify-2fa-btn"
              >
                {loading ? (
                  <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Verifying...</>
                ) : (
                  <>
                    <ShieldCheck size={24} weight="bold" />
                    Verify & Sign In
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="w-full h-12 flex items-center justify-center gap-2 text-neutral-600 dark:text-neutral-400 font-bold hover:text-neutral-900 dark:hover:text-white transition-colors"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft size={20} weight="bold" />
                Back to login
              </button>
            </form>
          ) : (
            /* Normal Login */
            <>
              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full h-14 flex items-center justify-center gap-3 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-white font-bold text-lg rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] hover:shadow-[4px_4px_0px_#000] dark:hover:shadow-[4px_4px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all mb-6"
                data-testid="google-login-btn"
              >
                <GoogleLogo size={24} weight="bold" className="text-[#4285F4]" />
                Continue with Google
              </button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-neutral-200 dark:border-neutral-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 font-bold">or sign in with email</span>
                </div>
              </div>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Email Address</Label>
                  <div className="relative">
                    <EnvelopeSimple size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="password" className="text-lg font-bold text-neutral-900 dark:text-white">Password</Label>
                    <Link to="/forgot-password" className="text-[#2B4C6F] font-bold hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      required
                      className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-5 h-5 rounded border-2 border-black dark:border-neutral-600 accent-[#2B4C6F]"
                    data-testid="remember-me-checkbox"
                  />
                  <label htmlFor="remember" className="text-neutral-600 dark:text-neutral-400 font-medium cursor-pointer">
                    Remember me for 30 days
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Signing in...</>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          )}

          {!requires2FA && (
            <p className="mt-6 text-center text-neutral-600 dark:text-neutral-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#2B4C6F] font-bold hover:underline">
                Sign up
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
